/**
 * Advanced Native Share Button — Godha World (v9: floating icon inside PDP image box)
 * - Mobile: native share sheet | Desktop/in-app browsers: popup (WhatsApp, Telegram, X, Facebook, email, copy)
 * - Inline button visible (inline-flex fix) + floating round share icon inside each product image (top-right)
 * - Price / stock / URL follow the selected variant at share time
 * - Copy-link fallback when navigator.clipboard is unavailable
 */
if (!customElements.get('share-button')) {
  customElements.define(
    'share-button',
    class ShareButton extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this._init();
      }

      _init() {
        this.nativeBtn = this.querySelector('[data-share-native]');
        this.desktopPanel = this.querySelector('[data-share-desktop]');
        this.copyBtn = this.querySelector('[data-share-copy]');
        this.toast = this.querySelector('[data-share-toast]');
        this.overlay = this.querySelector('[data-share-overlay]');
        this.urlInput = this.querySelector('.gw-share-copybar__url');

        this.productTitle = this.dataset.shareTitle || document.title;
        this.baseUrl = this.dataset.shareUrl || window.location.href.split('?')[0];
        this.basePrice = this.dataset.sharePrice || '';
        this.baseAvailable = this.dataset.shareAvailable === 'true';

        this._refresh();

        if (navigator.share) {
          if (this.nativeBtn) {
            this.nativeBtn.style.display = 'inline-flex';
            this.nativeBtn.addEventListener('click', (e) => this._handleNativeShare(e));
          }
          if (this.desktopPanel) this.desktopPanel.remove();
          if (this.overlay) this.overlay.remove();
        } else {
          if (this.nativeBtn) {
            this.nativeBtn.style.display = 'inline-flex';
            this.nativeBtn.addEventListener('click', (e) => this._toggleDesktop(e));
          }
          if (this.overlay) this.overlay.addEventListener('click', () => this._closeDesktop());
          document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this._closeDesktop();
          });
        }

        if (this.copyBtn) this.copyBtn.addEventListener('click', () => this._copyLink());
      }

      _currentVariant() {
        let id = new URLSearchParams(window.location.search).get('variant');
        if (!id) {
          const input = document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
          if (input && input.value) id = input.value;
        }
        if (!id) return null;
        const scripts = document.querySelectorAll(
          'variant-radios script[type="application/json"], variant-selects script[type="application/json"]'
        );
        for (const s of scripts) {
          try {
            const list = JSON.parse(s.textContent);
            if (Array.isArray(list)) {
              const v = list.find((x) => String(x.id) === String(id));
              if (v) return v;
            }
          } catch (err) {}
        }
        return null;
      }

      _formatPrice(paise) {
        const rupees = paise / 100;
        const opts = Number.isInteger(rupees)
          ? { maximumFractionDigits: 0 }
          : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
        return '₹' + rupees.toLocaleString('en-IN', opts);
      }

      _refresh() {
        const v = this._currentVariant();
        this.sharePrice = v ? this._formatPrice(v.price) : this.basePrice;
        this.shareAvailable = v ? !!v.available : this.baseAvailable;
        this.shareUrl = v ? this.baseUrl.split('?')[0] + '?variant=' + v.id : this.baseUrl;
        this.shareText = this._buildShareText();

        if (this.urlInput) this.urlInput.value = this.shareUrl;

        const encodedText = encodeURIComponent(this.shareText + '\n' + this.shareUrl);
        const encodedUrl = encodeURIComponent(this.shareUrl);
        const encodedTitle = encodeURIComponent(this.productTitle);
        this.querySelectorAll('[data-share-app]').forEach((link) => {
          switch (link.dataset.shareApp) {
            case 'whatsapp':
              link.href = `https://api.whatsapp.com/send?text=${encodedText}`;
              break;
            case 'telegram':
              link.href = `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(this.shareText)}`;
              break;
            case 'twitter':
              link.href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(this.shareText)}&url=${encodedUrl}`;
              break;
            case 'facebook':
              link.href = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
              break;
            case 'email':
              link.href = `mailto:?subject=${encodedTitle}&body=${encodedText}`;
              break;
          }
        });
      }

      _buildShareText() {
        let text = this.sharePrice
          ? `🛒 Check this out — ${this.productTitle} at just ${this.sharePrice}`
          : `🛒 Check this out — ${this.productTitle}`;
        if (this.shareAvailable) text += ' ✅ In Stock!';
        text += '\n\nShop now on Godha World 👇';
        return text;
      }

      _handleNativeShare(e) {
        this._ripple(e);
        this._refresh();
        navigator.share({ title: this.productTitle, text: this.shareText, url: this.shareUrl }).catch(() => {});
      }

      _toggleDesktop(e) {
        if (this.desktopPanel && this.desktopPanel.classList.contains('gw-share-panel--open')) {
          this._closeDesktop();
        } else {
          this._openDesktop(e);
        }
      }

      _openDesktop(e) {
        this._ripple(e);
        this._refresh();
        if (this.desktopPanel) this.desktopPanel.classList.add('gw-share-panel--open');
        if (this.overlay) this.overlay.classList.add('gw-share-overlay--open');
      }

      _closeDesktop() {
        if (this.desktopPanel) this.desktopPanel.classList.remove('gw-share-panel--open');
        if (this.overlay) this.overlay.classList.remove('gw-share-overlay--open');
      }

      _copyLink() {
        this._refresh();
        const done = () => {
          if (this.copyBtn) {
            this.copyBtn.classList.add('gw-share-copied');
            const label = this.copyBtn.querySelector('span');
            const origText = label ? label.textContent : '';
            if (label) label.textContent = 'Copied!';
            setTimeout(() => {
              this.copyBtn.classList.remove('gw-share-copied');
              if (label) label.textContent = origText;
            }, 2000);
          }
          this._showToast();
        };
        const fallback = () => {
          try {
            const input = this.urlInput || document.createElement('input');
            if (!this.urlInput) {
              input.value = this.shareUrl;
              document.body.appendChild(input);
            }
            input.select();
            input.setSelectionRange(0, 99999);
            const ok = document.execCommand('copy');
            if (!this.urlInput) input.remove();
            if (ok) done();
          } catch (err) {}
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(this.shareUrl).then(done).catch(fallback);
        } else {
          fallback();
        }
      }

      _showToast() {
        if (!this.toast) return;
        this.toast.classList.add('gw-share-toast--show');
        setTimeout(() => this.toast.classList.remove('gw-share-toast--show'), 2500);
      }

      _ripple(e) {
        const btn = e.currentTarget;
        const circle = document.createElement('span');
        const rect = btn.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        circle.style.width = circle.style.height = size + 'px';
        circle.style.left = e.clientX - rect.left - size / 2 + 'px';
        circle.style.top = e.clientY - rect.top - size / 2 + 'px';
        circle.classList.add('gw-share-ripple');
        btn.appendChild(circle);
        setTimeout(() => circle.remove(), 600);
      }
    }
  );
}

/* ---- Floating share icon inside each PDP image box (position set in Theme settings > Product share icon) ---- */
(function () {
  function addFloatingShare() {
    const source = document.querySelector('share-button.gw-share:not(.gw-share--float)');
    const boxes = document.querySelectorAll('.product__media-item .product__media');
    if (!source || !boxes.length) return;

    if (!document.getElementById('gw-share-float-css')) {
      const style = document.createElement('style');
      style.id = 'gw-share-float-css';
      /* The theme stretches every direct child of .product__media to 100% x 100%, so override with !important */
      style.textContent =
        '.product__media > .gw-share.gw-share--float{position:absolute!important;top:10px!important;right:10px!important;' +
        'left:auto!important;bottom:auto!important;width:auto!important;height:auto!important;margin:0!important;' +
        'z-index:6!important;display:inline-flex!important}' +
        '.gw-share--float .gw-share__trigger{width:38px;height:38px;padding:0;gap:0;justify-content:center;' +
        'background:rgba(255,255,255,.95);border:none;color:#1a1a1a;box-shadow:0 2px 10px rgba(0,0,0,.18)}' +
        '.gw-share--float .gw-share__trigger span:not(.gw-share-ripple){display:none}' +
        '.gw-share--float .gw-share__trigger svg{width:19px;height:19px}' +
        '.gw-share--float .gw-share__trigger:hover{background:#1a1a1a;color:#fff}' +
        '@media (min-width:750px){' +
        '.gw-share--float .gw-share-panel{bottom:auto;top:calc(100% + 10px);left:auto;right:0;transform:translateY(-8px)}' +
        '.gw-share--float .gw-share-panel--open{transform:translateY(0)}' +
        '.gw-share--float .gw-share-panel::after{display:none}}';
      document.head.appendChild(style);
    }

    boxes.forEach((box) => {
      if (box.querySelector('.gw-share--float')) return;
      const clone = source.cloneNode(true);
      clone.classList.add('gw-share--float');
      clone.removeAttribute('id');
      clone.querySelectorAll('.gw-share-ripple').forEach((n) => n.remove());
      const panel = clone.querySelector('[data-share-desktop]');
      if (panel) panel.classList.remove('gw-share-panel--open');
      const overlay = clone.querySelector('[data-share-overlay]');
      if (overlay) overlay.classList.remove('gw-share-overlay--open');
      const trigger = clone.querySelector('[data-share-native]');
      if (trigger) trigger.setAttribute('aria-label', 'Share this product');
      /* keep taps on the icon from opening the image zoom */
      clone.addEventListener('click', (e) => e.stopPropagation());
      if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
      box.appendChild(clone);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addFloatingShare);
  } else {
    addFloatingShare();
  }
})();
